import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { icons, type IconSlot } from '@/lib/iconography';
import { cn } from '@/lib/utils';

type Entity = 'session' | 'note' | 'case_report' | 'assignment' | 'exercise' | 'red_flag';

type Bucket =
  | 'passive'
  | 'active'
  | 'needs-action'
  | 'terminal-positive'
  | 'terminal-negative'
  | 'crisis';

interface StatusDescriptor {
  bucket: Bucket;
  label: string;
}

/**
 * (entity, status) → bucket + label. Single source of truth; mirror to Part A
 * §4.2 when adding new statuses. Keep labels in sentence case (voice rule).
 */
const statusMap: Record<Entity, Record<string, StatusDescriptor>> = {
  session: {
    scheduled: { bucket: 'passive', label: 'Scheduled' },
    in_progress: { bucket: 'active', label: 'In progress' },
    completed: { bucket: 'terminal-positive', label: 'Completed' },
    no_show: { bucket: 'terminal-negative', label: 'No show' },
    cancelled: { bucket: 'terminal-negative', label: 'Cancelled' },
    rescheduled: { bucket: 'passive', label: 'Rescheduled' },
  },
  note: {
    draft: { bucket: 'passive', label: 'Draft' },
    submitted: { bucket: 'terminal-positive', label: 'Submitted' },
  },
  case_report: {
    draft: { bucket: 'passive', label: 'Draft' },
    pending_review: { bucket: 'needs-action', label: 'Pending review' },
    approved: { bucket: 'terminal-positive', label: 'Approved' },
    rejected: { bucket: 'terminal-negative', label: 'Rejected' },
    escalated_to_chief: { bucket: 'needs-action', label: 'Escalated to chief' },
  },
  assignment: {
    assigned: { bucket: 'passive', label: 'Assigned' },
    // Client-side vocab: BE emits `pending` for newly-assigned rows the
    // client hasn't opened yet, and `completed` once they submit. Map
    // both into existing buckets so the chip reads as deliberate
    // (instead of the defensive raw-string fallback).
    pending: { bucket: 'passive', label: 'Pending' },
    in_progress: { bucket: 'active', label: 'In progress' },
    submitted: { bucket: 'needs-action', label: 'Submitted' },
    completed: { bucket: 'terminal-positive', label: 'Completed' },
    scored: { bucket: 'terminal-positive', label: 'Scored' },
  },
  exercise: {
    // Staff-path vocab (`src/features/exercises/` → /clients/:id/exercises).
    assigned: { bucket: 'passive', label: 'Assigned' },
    in_progress: { bucket: 'active', label: 'In progress' },
    submitted: { bucket: 'needs-action', label: 'Submitted' },
    reviewed: { bucket: 'terminal-positive', label: 'Reviewed' },
    // Client-path vocab (self-help → /engagement/self-help/exercises). The
    // two paths emit different status strings for the same underlying
    // lifecycle; both map into the same chip buckets.
    pending: { bucket: 'passive', label: 'Pending' },
    completed: { bucket: 'needs-action', label: 'Completed' },
  },
  red_flag: {
    crisis: { bucket: 'crisis', label: 'High risk' },
    acute_risk: { bucket: 'needs-action', label: 'At risk' },
    resolved: { bucket: 'terminal-positive', label: 'Resolved' },
    closed: { bucket: 'passive', label: 'Closed' },
  },
};

/** Resolves the icon slot for an entity/status pair. Module-level so it is
 *  stable — avoids react/no-unstable-nested-components. */
function resolveIcon(entity: Entity, status: string): IconSlot {
  switch (entity) {
    case 'session':
      return icons.sessionStatus[status as keyof typeof icons.sessionStatus];
    case 'case_report':
      return icons.caseReportStatus[status as keyof typeof icons.caseReportStatus];
    case 'assignment':
      return icons.assignmentStatus[status as keyof typeof icons.assignmentStatus];
    case 'exercise':
      return icons.exerciseStatus[status as keyof typeof icons.exerciseStatus];
    case 'red_flag':
      return icons.flagSeverity[status as keyof typeof icons.flagSeverity];
    case 'note':
      // Notes have no leading status icon by design — the chip's text label
      // carries the full meaning. Explicit return keeps this intentional.
      return null;
    default:
      return null;
  }
}

const chipVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-body-sm font-medium',
  {
    variants: {
      bucket: {
        passive: 'bg-neutral-100 text-neutral-600',
        active: 'bg-primary-50 text-primary-700',
        'needs-action': 'bg-warning-50 text-warning-700',
        'terminal-positive': 'bg-primary-500 text-primary-foreground',
        'terminal-negative': 'bg-error-50 text-error-700',
        crisis: 'bg-crisis-50 text-crisis-500 pl-1.5 border-l-2 border-crisis-500',
      },
    },
    defaultVariants: {
      bucket: 'passive',
    },
  },
);

type StatusChipProps = VariantProps<typeof chipVariants> & {
  entity: Entity;
  status: string;
  iconless?: boolean;
  className?: string;
};

/**
 * Maps (entity, status) → bucket, icon, and optional pulse. Non-interactive.
 * Decorative icons + pulse are aria-hidden; the chip's text is the semantic
 * label.
 */
export function StatusChip({ entity, status, iconless = false, className }: StatusChipProps) {
  const descriptor = statusMap[entity][status];
  if (!descriptor) {
    // Defensive: render a passive chip with the raw status so unmapped values
    // are visible during development rather than silently missing.
    return (
      <span data-bucket="passive" className={cn(chipVariants({ bucket: 'passive' }), className)}>
        {status}
      </span>
    );
  }

  const { bucket, label } = descriptor;

  const isPulse =
    (entity === 'session' || entity === 'assignment' || entity === 'exercise') &&
    status === 'in_progress';

  const iconSlot = !iconless && !isPulse ? resolveIcon(entity, status) : null;

  return (
    <span data-bucket={bucket} className={cn(chipVariants({ bucket }), className)}>
      {bucket === 'crisis' ? <span data-crisis-accent aria-hidden="true" /> : null}
      {isPulse ? (
        <span data-pulse aria-hidden="true" className="inline-flex items-center gap-0.5">
          <span className="chip-pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-current" />
          <span
            className="chip-pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-current"
            style={{ animationDelay: '700ms' }}
          />
        </span>
      ) : null}
      {iconSlot !== null
        ? React.createElement(iconSlot, { 'aria-hidden': 'true', className: 'h-3.5 w-3.5' })
        : null}
      {label}
    </span>
  );
}
